<template>
  <div>
    <b-modal
      v-model="isOpened"
      title="Flipbook Management"
      centered
      :ok-title="activeTab === 1 ? 'Go to Flipbook' : ''"
      :ok-disabled="activeTab === 1 ? !(handledCatalog || selectedCatalog) : true"
      :hide-footer="activeTab === 0"
      @ok="goToCatalog"
      @cancel="onClose"
    >
      <b-tabs
        v-model="activeTab"
        content-class="mt-3"
        nav-class="custom-tabs"
      >
        <b-tab
          title="Create Flipbook"
          active
          @click="onTabClick"
        >
          <div role="group">
            <label for="input-live">Name</label>
            <b-form-input
              v-model="name"
              :state="nameState"
              placeholder="Enter name"
              aria-describedby="input-live-feedback"
              trim
            />
            <b-form-invalid-feedback id="input-live-feedback">
              Enter a name of at least 1 and less than 255 chars.
            </b-form-invalid-feedback>
          </div>

          <div class="brand-section mt-3">
            <div class="brand-header">
              <span class="brand-label">Brand</span>
              <label class="brand-default-check">
                <input
                  v-model="setAsDefault" 
                  type="checkbox"
                >
                <span>Set as default</span>
              </label>
            </div>
        
            <div class="brand-row">
              <div class="logo-box">
                <span v-if="!logoUrl">LOGO</span>
                <img 
                  v-else 
                  :src="logoUrl" 
                  alt="Logo preview" 
                >
              </div>
        
              <button 
                type="button" 
                class="tb-action" 
                @click="triggerFileInput"
              >
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  stroke-width="2" 
                  stroke-linecap="round" 
                  stroke-linejoin="round"
                >
                  <path d="M12 19V5" />
                  <path d="M5 12l7-7 7 7" />
                </svg>
                Upload
              </button>
        
              <button 
                type="button" 
                class="tb-action" 
                @click="openFileBrowser"
              >
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  stroke-width="2" 
                  stroke-linecap="round" 
                  stroke-linejoin="round"
                >
                  <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
                </svg>
                File Browser
              </button>
        
              <div
                v-click-outside="closePicker"
                class="brand-color-wrap"
              >
                <button
                  type="button"
                  class="brand-color-swatch"
                  :style="{ backgroundColor: brandColor }"
                  @click="pickerOpen = !pickerOpen"
                />
        
                <ColorPickerPopover
                  v-if="pickerOpen"
                  v-model="brandColor"
                  class="brand-color-popover"
                />
              </div>
            </div>
        
            <input
              ref="fileInput"
              type="file"
              accept="image/*"
              class="d-none"
              @change="onFileSelected"
            >
          </div>

          <b-form-group
            class="mt-3 catalog-cover-upload"
          >
            <input
              ref="coverImageInput"
              type="file"
              accept="image/*"
              class="d-none"
              @change="onCoverImageChange"
            >

            <div class="catalog-cover-upload__content d-flex align-items-center justify-content-between gap-2">
              <div class="d-flex align-items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-primary catalog-cover-upload__icon-info"
                >
                  <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                  <path d="M20 3v4" /><path d="M22 5h-4" />
                  <path d="M4 17v2" /><path d="M5 18H3" />
                </svg>

                <span class="font-sm">
                  {{ coverImage
                    ? 'Cover auto-generated from custom upload'
                    : 'Cover auto-generated from first layout'
                  }}
                </span>
              </div>

              <button
                type="button"
                class="btn btn-link p-0 font-sm"
                @click="$refs.coverImageInput.click()"
              >
                <i class="bi bi-upload mr-1 catalog-cover-upload__icon-upload" />
                {{ coverImage ? 'Replace' : 'Upload custom' }}
              </button>
            </div>
          </b-form-group>

          <div
            role="group"
            class="mt-3"
          >
            <div class="d-flex align-items-center justify-content-between gap-2">
              <label class="mb-0">Password</label>

              <b-form-checkbox
                v-model="requirePassword"
                class="mr-3"
              >
                Require password to view
              </b-form-checkbox>
            </div>

            <b-form-input
              v-model="password"
              class="mt-2"
              :state="passwordState"
              :disabled="!requirePassword"
              placeholder="Password"
            />

            <b-form-invalid-feedback v-if="requirePassword">
              Password must be at least 4 characters.
            </b-form-invalid-feedback>
          </div>

          <div class="mt-3 d-flex justify-content-end align-center gap-2">
            <b-button
              v-b-tooltip.hover
              variant="link"
              class="text-dark"
              title="Close"
              @click="onClose"
            >
              Cancel
            </b-button>

            <b-button
              v-b-tooltip.hover
              variant="primary"
              :disabled="!nameState || (requirePassword && !passwordState)"
              title="Create and copy link to clipboard"
              @click="createAndCopy"
            >
              <i class="bi bi-copy" />
              Create &amp; copy link
            </b-button>

            <b-button
              v-b-tooltip.hover
              variant="outline-primary"
              :disabled="!nameState || (requirePassword && !passwordState)"
              title="Create and open in new tab"
              @click="createAndOpen"
            >
              <i class="bi bi-box-arrow-up-right" />
            </b-button>
          </div>
        </b-tab>
        <b-tab
          title="Add to Flipbook"
          @click="onTabClick"
        >
          <b-pagination
            v-model="catalogsPage"
            :total-rows="catalogsCount"
            :per-page="catalogsPerPage"
            @change="getCatalogList"
          />
          <b-list-group>
            <b-list-group-item
              v-for="catalog in catalogs"
              :key="catalog.id"
              class="cursor-pointer d-flex justify-content-between catalog-item"
              :active="selectedCatalog === catalog"
              @click="selectedCatalog = catalog"
            >
              {{ catalog.name }}
              <div class="catalog-item__buttons">
                <b-button
                  class="btn-sm"
                  variant="outline-dark"
                  @click.stop="onRenameBtnClick(catalog)"
                >
                  <i class="bi bi-pencil-square" />
                </b-button>
                <b-button
                  class="btn-sm"
                  variant="outline-danger"
                  @click.stop="deleteCatalog(catalog.id)"
                >
                  <i class="bi bi-trash" />
                </b-button>
              </div>
            </b-list-group-item>
          </b-list-group>
          <b-button
            class="mt-3"
            variant="primary"
            :disabled="!selectedCatalog"
            @click="addToCatalog"
          >
            Add to selected Flipbook
          </b-button>
        </b-tab>
      </b-tabs>
    </b-modal>
    <EditCatalogModal
      :catalog="selectedCatalog"
      @catalogUpdated="onCatalogUpdated"
    />
    <SelectFolderModal
      :mode="'select'"
      @onSelectedItems="onSelectedItems"
    />
  </div>
</template>

<script>
import ClickOutside from 'vue-click-outside';
import {
  CATALOG_MANAGEMENT_EVENTS,
  catalogManagementEventBroker
} from "@frontend/group/modules/catalog-management/event-broker";
import CatalogService from '@/frontend/services/api/catalogs'
import EditCatalogModal from "@frontend/components/modules/layouts-page/modals/EditCatalogModal.vue";
import ColorPickerPopover from "../ColorPickerPopover.vue";
import SelectFolderModal from "@frontend/components/modules/file-browser/modals/SelectFolderModal.vue";

const initialState = () => ({
  isOpened: false,
  name: 'New Flipbook',
  layoutIds: [],
  catalogs: [],
  selectedCatalog: undefined,
  catalogsPage: 1,
  catalogsPerPage: undefined,
  catalogsCount: undefined,
  handledCatalog: undefined,
  isLivewireUpdated: false,
  requirePassword: false,
  password: '',
  activeTab: 0,
  coverImage: null,
  setAsDefault: false,
  logoUrl: null,
  brandColor: "#111725",
  pickerOpen: false,
  logoFile: null,
  isDefaultBrandLoaded: false,
})
export default {
  name: "CatalogManagementModal",
  components: { EditCatalogModal, ColorPickerPopover, SelectFolderModal },
  directives: {
    ClickOutside,
  },
  data: initialState,
  computed: {
    nameState() {
      return this.name.length <= 255 && this.name.length > 0
    },
    passwordState() {
      if (!this.requirePassword) {
        return null;
      }

      if (!this.password) {
        return false;
      }

      return this.password.length >= 4 && this.password.length <= 255;
    }
  },
  watch: {
    requirePassword(val) {
      if (!val) {
        this.password = '';
      }
    }
  },
  mounted() {
    catalogManagementEventBroker.on(CATALOG_MANAGEMENT_EVENTS.INIT, this.init.bind(this))
    catalogManagementEventBroker.on('openModalEdit', (config) => {
      this.isLivewireUpdated = true;
      this.selectedCatalog = {
        id: config.id,
        name: config.name,
        isProtected: config.isProtected !== 'false',
        coverImage: config.coverImage,
        brand_logo: config.brandLogo,
        brandColor: config.brandColor,
        setAsDefault: config.setAsDefault,
      }
      this.$bvModal.show('edit-catalog-modal');
    })
  },
  methods: {
    init({ layoutIds }) {
      this.isOpened = true;
      this.layoutIds = layoutIds;

      this.getCatalogList(this.catalogsPage);
    },
    async getCatalogList(page) {
      const { data } = await CatalogService.list(page)
      this.catalogs = data.catalogs;
      this.catalogsCount = data.count;
      this.catalogsPerPage = data.perPage;

      if (!this.isDefaultBrandLoaded) {
        this.brandColor = data.defaultBrand?.brandColor || this.brandColor;
        this.isDefaultBrandLoaded = true;
      }
    },
    async create(event) {
      if (event) {
        event.preventDefault();
      }

      const formData = new FormData();

      formData.append('name', this.name);
      formData.append('password', this.requirePassword ? this.password : '');
      formData.append('brandColor', this.brandColor);
      formData.append('setAsDefault', this.setAsDefault ? '1' : '0');

      this.layoutIds.forEach(id => {
        formData.append('layoutIds[]', id);
      });

      if (this.coverImage) {
        formData.append('coverImage', this.coverImage);
      }

      if (this.logoFile) {
        formData.append('logoFile', this.logoFile);
      }

      const { data } = await CatalogService.create(formData);

      this.handledCatalog = data;

      await this.getCatalogList(this.catalogsPage);

      toastr.success('Flipbook created');
      Livewire.emit('refreshCatalogs');

      window.dispatchEvent(new CustomEvent('catalog-created-from-selection'));

      return data;
    },
    async createAndCopy() {
      try {
        const data = await this.create();
        const url = `${window.location.origin}/catalogs/${data.slug}`;

        await navigator.clipboard.writeText(url);

        toastr.success('Link copied to clipboard');
        this.onClose();
      } catch (error) {
        toastr.error('Something went wrong');
      }
    },
    async createAndOpen() {
      try {
        const data = await this.create();
        const url = `/catalogs/${data.slug}`;

        window.open(url, '_blank');
        this.onClose();
      } catch (error) {
        toastr.error('Something went wrong');
      }
    },
    async addToCatalog() {
      try {
        const formData = new FormData();

        this.layoutIds.forEach(id => {
          formData.append('layoutIds[]', id);
        });

        await CatalogService.update(this.selectedCatalog.id, formData);

        this.handledCatalog = this.selectedCatalog;

        toastr.success('Flipbook updated')
      } catch (error) {
        toastr.error('Something went wrong')
      }
    },
    goToCatalog() {
      const lnk = document.createElement('a');
      lnk.href = `/catalogs/${this.handledCatalog?.slug || this.selectedCatalog.slug}`;
      lnk.target = 'blank';
      lnk.click();
      this.onClose();
    },
    onClose() {
      const initial = initialState();
      Object.keys(initial).forEach(key => {
        this[key] = initial[key]
      });
    },
    onTabClick() {
      const initial = initialState();
      this.selectedCatalog = initial.selectedCatalog;
      this.handledCatalog = initial.handledCatalog;
      this.name = initial.name;
    },
    onRenameBtnClick(catalog) {
      this.selectedCatalog = catalog;
      this.$bvModal.show('edit-catalog-modal');
    },
    async deleteCatalog(catalogId) {
      try {
        const { value } = await Swal.fire({
          title: "Do you want to delete the catalog?",
          showCancelButton: true,
          confirmButtonText: "Confirm",
          cancelButtonText: "Cancel",
          icon: "warning",
        });
        
        if (value) {
          await CatalogService.delete(catalogId);
          await this.getCatalogList(this.catalogsPage);
          
          toastr.success('Flipbook deleted');
        }
        
      } catch (error) {
        toastr.error('Something went wrong')
      }
    },
    async onCatalogUpdated() {
      this.selectedCatalog = undefined;
      
      await this.getCatalogList(this.catalogsPage);
      
      toastr.success('Flipbook updated');

      if (this.isLivewireUpdated) {
        Livewire.emit('refreshCatalogs');
      }
    },
    onCoverImageChange(event) {
      const [file] = event.target.files;

      this.coverImage = file || null;
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    onFileSelected(e) {
      const file = e.target.files[0];
      this.logoFile = file || null;

      if (file) {
        this.logoUrl = URL.createObjectURL(file);
      }
    },
    openFileBrowser() {
      this.$bvModal.show('select-folder-modal')
    },
    closePicker() {
      this.pickerOpen = false;
    },
    async onSelectedItems([item] = []) {
      if (!item || item.type === 'folder') return;

      const fileUrl = `${window.location.origin}/share?file=${item.original_path}`;
      this.logoUrl = fileUrl;

      try {
        const response = await fetch(fileUrl);

        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`);
        }

        const blob = await response.blob();
        this.logoFile = new File([blob], item.name, { type: blob.type });
      } catch (error) {
        this.logoUrl = null;
        this.logoFile = null;
        console.error("Error processing file from browser:", error);
        toastr.error(`Failed to process file: ${item.name}`);
      }
    },
  }
}
</script>

<style scoped lang="scss">
::v-deep .modal-header {
  padding-bottom: 0;
  border: none;
}

::v-deep .modal-title {
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1;
}

::v-deep label,
::v-deep .custom-control-label {
  font-size: .875rem;
  font-weight: 500;
}

::v-deep .form-control,
::v-deep .form-control::placeholder {
  font-size: .875rem;
  line-height: 1.25rem;
}

::v-deep .custom-tabs {
  gap: 1rem;

  .nav-item {
    margin-bottom: -2px;
  }

  .nav-link {
    padding: .375rem .25rem .5rem;
    border: none;
    border-bottom: 2px solid transparent;
    background: transparent;
    color: #64748b;
    font-size: .875rem;
    line-height: 1.25rem;
    transition: all .3s ease;

    &:hover,
    &.active {
      border-bottom-color: #209c86;
      color: #3c4b64;
    }
  }
}

.catalog-cover-upload {
  padding: .5rem .75rem;
  border-radius: .375rem;
  background-color: rgba(241, 245, 249, .4);

  &__content {
    gap: .75rem;
  }

  &__icon-info {
    width: .875rem;
    height: .875rem;
  }

  &__icon-upload {
    width: .75rem;
    height: .75rem;
  }
}

.catalog-item {
  &__buttons {
    opacity: 0;
    visibility: hidden;
    transition: all ease .3s;
  }

  &:hover {
    .catalog-item__buttons {
      opacity: 1;
      visibility: visible;
    }
  }
}
.brand-section {
  font-family: inherit;
}
 
.brand-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
 
.brand-label {
  font-weight: 500;
  font-size: 0.875rem;
}
 
.brand-default-check {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  user-select: none;
}
.brand-default-check input[type="checkbox"] {
  accent-color: #16a34a;
  width: 15px;
  height: 15px;
  cursor: pointer;
}
 
.brand-row {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 12px;
}
 
.logo-box {
  flex: none;
  width: 40px;
  height: 40px;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6rem;
  font-weight: 600;
  color: #9ca3af;
  overflow: hidden;
  background: #fafafa;
}
.logo-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
 
.tb-action {
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: #374151;
  font-size: 0.85rem;
  padding: 6px 4px;
  cursor: pointer;
  white-space: nowrap;
}
.tb-action:hover {
  color: #111827;
}
 
.brand-color-wrap {
  position: relative;
  margin-left: auto;
}
 
.brand-color-swatch {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;
  padding: 0;
}
 
.brand-color-popover {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 1000;
}
</style>
